import { capitalize } from "./strings";
import { InfoResponse, Information, File } from "@/types";
import { useStore } from "@/store";
import { Store } from "vuex";

enum Event {
  INFO = "Info",
  REBOOT = "Reboot",
  EXCEPT = "Except",
  SET_OK = "SetOK",
  PING = "Ping",
  PONG = "Pong",
  WAITING = "Waiting",
  UPLOAD = "Upload",
  UPLOADING = "Uploading",
}

enum Command {
  GET_INFO = "info",
  SET_TIMER = "enableCron",
  SET_INTERVAL = "sendTime",
  SET_FOLDER = "folderId",
  SNAPSHOT = "snapshot",
}

export class WebEye {
  timer: boolean;
  interval?: number;
  folderId?: string;
  scriptId?: string;

  private _topic: string;
  private _store: Store<Information>;
  private _client: any; // eslint-disable-line

  constructor(topic: string) {
    this.timer = false;
    this._topic = topic;
    this._client = new window.webduino.module.mqttClient();
    this._store = useStore();
  }

  async connect() {
    await this._client.connect();
    this._getInfo();
    await this._client.onMessage(
      `${this._topic}/state`,
      async (message: string) => this._callback(message)
    );
  }

  snapshot(): void {
    this._publish(Command.SNAPSHOT);
  }

  setFolder(): void {
    this._publish(Command.SET_FOLDER, this._store.state.folderId);
  }

  setTimer(): void {
    this._publish(
      Command.SET_TIMER,
      this._store.state.timer ? "True" : "False"
    );
    this._publish(Command.SET_INTERVAL, this._store.state.interval?.toString());
  }

  private _callback(message: string): void {
    const [event, data] = this._parseMessage(message);
    this[`_handle${event}`](data);
  }

  private _parseMessage(message: string): [Event, string] {
    const index = message.trim().indexOf(" ");
    return index > 0
      ? [capitalize(message.slice(0, index)) as Event, message.slice(index + 1)]
      : [capitalize(message) as Event, ""];
  }

  private _publish(command: Command, message?: string): void {
    this._client.send({
      topic: `${this._topic}/${command}`,
      message: message || "",
    });
  }

  private _getInfo(): void {
    this._publish(Command.GET_INFO);
  }

  private _resetTitle(): void {
    setTimeout(() => {
      this._store.commit("resetTitle");
    }, 2000);
  }

  private _setTitle(title: string): void {
    this._store.commit("setTitle", title);
  }

  private _setFile(file: File): void {
    this._store.dispatch("setFile", file);
  }

  private _loading(): void {
    this._store.commit("loading");
  }

  private _handleInfo(message: string): void {
    const info = JSON.parse(
      message
        .replace("True", "true")
        .replace("False", "false")
        .replaceAll("'", '"')
    ) as InfoResponse;
    this._store.commit("setInfo", info);
  }

  private _handleReboot(): void {
    this._setTitle("WebEye Pro 重新開機...");
    this._resetTitle();
  }

  private _handleExcept(): void {
    this._setTitle("發生錯誤，重新開機中...");
    this._resetTitle();
  }

  private _handleSetOK(message: string): void {
    switch (message) {
      case Command.SET_FOLDER:
        this._setTitle("資料夾更新完成");
        this._resetTitle();
        break;
      case Command.SET_INTERVAL:
        this._setTitle("定時設定更新完成");
        this._resetTitle();
    }
  }

  private _handlePing(): void {
    this._setTitle("連線中...");
  }

  private _handlePong(): void {
    this._setTitle("連線完成");
  }

  private _handleWaiting(): void {
    this._setTitle("WebEye Pro 拍照中...");
  }

  private _handleUpload(message: string): void {
    const file = JSON.parse(message.replaceAll("'", '"')) as File;
    this._setTitle("照片讀取中...");
    this._setFile(file);
  }

  private _handleUploading(): void {
    this._setTitle("照片讀取中...");
    this._loading();
  }
}
